/**
 * Above-threshold gate definition (UI/UX spec §6.3, §8.2).
 *
 * A spend ask above the compliance threshold is "routed to Stratus": Stratus must
 * clear it before the contractor lead can approve it (see governance.useSpendAskPermissions
 * and the two approval queues). This module is the single source of truth for what counts
 * as above-threshold.
 *
 * Kept as a leaf (types-only import) so mutations.ts, governance.ts and the queue screens
 * can all share it without an import cycle.
 */
import type { SpendRequest } from "./types";

/** Uganda Sovereignty pack threshold: single spend at/above this routes to Stratus. */
export const SPEND_STRATUS_GATE_UGX = 400_000_000;

/**
 * Whether an ask is above the Stratus gate. True when it carries a stratus-routed
 * threshold flag (covers deliberately-flagged seed asks such as fire-stations `sr-007`)
 * OR its amount is at/above the 400M gate (covers every newly-raised ask — `raiseSpend`
 * sets the flag at exactly this line).
 */
export function isAboveStratusThreshold(
  ask: Pick<SpendRequest, "amount" | "thresholdFlag">,
): boolean {
  return ask.thresholdFlag?.routedTo === "stratus" || ask.amount >= SPEND_STRATUS_GATE_UGX;
}

/**
 * Amount above which the compliance pack requires a written reason on EVERY
 * spend-request state change (approve, reject, confirm, commit) — not just a
 * rejection. Mirrors `approval.reasonRequiredAboveAmount` in
 * server/compliance/packs/uganda-sovereignty-1.0.0.js, the same way
 * governance.ROLE_PERMISSIONS mirrors the server role pack: if the pack
 * changes, change it there first and copy here.
 *
 * Deliberately NOT the same number as SPEND_STRATUS_GATE_UGX above, and the two
 * answer different questions:
 *   - 100M — this transition needs a written justification recorded against it;
 *   - 400M — this ask routes to Stratus for verification before the lead approves.
 *
 * The server enforces this in transitionSpendRequest (spend_request.js); it
 * throws, and the throw becomes a 500. This constant exists so the UI can
 * collect the reason BEFORE sending, rather than letting the action fail.
 * Approve and Confirm used to send no reason at all, so every ask over 100M —
 * which is every ask that clears the Stratus gate — failed silently on approve.
 */
export const SPEND_REASON_REQUIRED_ABOVE_UGX = 100_000_000;

/** Whether this ask's state changes must carry a written reason (pack rule above). */
export function requiresTransitionReason(ask: Pick<SpendRequest, "amount">): boolean {
  return ask.amount > SPEND_REASON_REQUIRED_ABOVE_UGX;
}
